import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { countryMatchesSearch } from "../utils/geoSelection";

const denseItemSx = {
  display: "flex",
  alignItems: "center",
  ml: 0,
  my: 0,
  "& .MuiCheckbox-root": { py: 0.25 },
  "& .MuiFormControlLabel-label": { my: 0 },
} as const;

type CountryOption = { code: string; name: string };

type GeoCountryCheckboxListProps = {
  title: string;
  action?: React.ReactNode;
  countries: CountryOption[];
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  placeholder?: string;
  selectedCodes: string[];
  onToggle: (countryCode: string, checked: boolean) => void;
  filterOutCodes?: string[];
  maxHeight?: number;
  disabled?: boolean;
  searchInputId?: string;
};

export const GeoCountryCheckboxList: FC<GeoCountryCheckboxListProps> = memo(
  ({
    title,
    action,
    countries,
    searchValue,
    onSearchChange,
    placeholder = "Search countries...",
    selectedCodes,
    onToggle,
    filterOutCodes = [],
    maxHeight = 220,
    disabled,
    searchInputId,
  }) => {
    const visibleCountries = countries
      .filter((c) =>
        searchValue !== undefined ? countryMatchesSearch(c, searchValue) : true
      )
      .filter((c) => !filterOutCodes.includes(c.code));

    return (
      <Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          {action}
        </Stack>

        {searchValue !== undefined && onSearchChange && (
          <TextField
            id={searchInputId}
            placeholder={placeholder}
            size="small"
            fullWidth
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={disabled}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 1 }}
          />
        )}

        <Box sx={{ maxHeight, overflow: "auto" }}>
          {visibleCountries.map((country) => {
            const isSelected = selectedCodes.includes(country.code);
            return (
              <FormControlLabel
                key={country.code}
                control={
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => onToggle(country.code, !isSelected)}
                    sx={{ pl: 0 }}
                    data-testid={`geo-country-checkbox-${country.code.toLowerCase()}`}
                  />
                }
                label={<Typography variant="body2">{country.name}</Typography>}
                sx={denseItemSx}
              />
            );
          })}
        </Box>
      </Box>
    );
  }
);

GeoCountryCheckboxList.displayName = "GeoCountryCheckboxList";
